import { SdkState, Announcement, AnnouncementView } from '../types';
import { getFromStorage, saveToStorage, removeFromStorage, hashKey } from '../utils/storage';

// Base names for the persisted keys. When an API key is supplied they are
// suffixed with a hash of it (see the constructor) so apps sharing an origin
// stay isolated; the bare names below double as the legacy (pre-namespacing)
// keys we migrate away from on first load.
const STATE_KEY = 'pulserelease_state';
const ANONYMOUS_ID_KEY = 'pulserelease_anonymous_id';
const SEEN_KEY = 'pulserelease_seen';
const SESSION_TOKEN_KEY = 'pulserelease_session_token';

/**
 * Cap on how many seen-announcement ids we retain in localStorage. The set only
 * needs to suppress re-display of announcements that are still live, so old ids
 * are safe to drop; keeping the most recent N bounds unbounded storage growth.
 */
const MAX_SEEN = 500;

export class State implements SdkState {
    public userId: string | null = null;
    public isAnonymous: boolean = true;
    public anonymousId: string | null = null;
    public sessionToken: string | null = null;
    public announcements: Announcement[] = [];
    public seenAnnouncements: Set<string> = new Set();
    public currentView: AnnouncementView | null = null;
    public isInitialized: boolean = false;
    public unreadCount: number = 0;
    public isFreePlan: boolean = true;
    public showBranding: boolean = true;

    // Per-apiKey namespaced storage keys, resolved in the constructor.
    private readonly stateKey: string;
    private readonly anonymousIdKey: string;
    private readonly seenKey: string;
    private readonly sessionTokenKey: string;
    private readonly namespaced: boolean;
    // Guest mode holds ZERO client-side state: no cookie, no localStorage, no
    // in-memory persistence beyond the current session's runtime flags. Every
    // storage-touching path below short-circuits when this is set, so guest
    // sessions never invoke the storage helpers at all.
    private readonly guest: boolean;

    /**
     * @param apiKey the application's public API key. Used only to derive a
     *   storage namespace so two apps on the same origin don't share (and
     *   clobber) each other's session token, anonymous id and seen-set. Omit it
     *   (tests) to fall back to the un-namespaced legacy keys.
     * @param guest when true, the state is session-less and never reads from or
     *   writes to storage — see {@link guest}.
     */
    constructor(apiKey?: string, guest: boolean = false) {
        this.guest = guest;
        this.namespaced = Boolean(apiKey);
        const suffix = apiKey ? `_${hashKey(apiKey)}` : '';
        this.stateKey = `${STATE_KEY}${suffix}`;
        this.anonymousIdKey = `${ANONYMOUS_ID_KEY}${suffix}`;
        this.seenKey = `${SEEN_KEY}${suffix}`;
        this.sessionTokenKey = `${SESSION_TOKEN_KEY}${suffix}`;

        // Guest mode never migrates or loads persisted identity — there is none.
        if (guest) {
            return;
        }

        this.migrateLegacyKeys();
        this.load();
    }

    public setUser(userId: string, isAnonymous: boolean): void {
        this.userId = userId?.toString();
        this.isAnonymous = isAnonymous;
        this.save();
    }

    public setAnonymousId(id: string): void {
        this.anonymousId = id;
        if (this.guest) {
            return;
        }
        saveToStorage(this.anonymousIdKey, id);
    }

    public getAnonymousId(): string | null {
        // Guest sessions have no persisted anonymous id and must never read storage.
        if (this.guest) {
            return this.anonymousId;
        }
        if (!this.anonymousId) {
            this.anonymousId = getFromStorage(this.anonymousIdKey);
        }
        return this.anonymousId;
    }

    public setAnnouncements(announcements: Announcement[]): void {
        this.announcements = announcements;
        this.save();
    }

    public markAsSeen(announcementId: string): void {
        this.seenAnnouncements.add(announcementId);
        this.saveSeen();
    }

    public markAllAsSeen(): void {
        this.announcements.forEach((a) => this.seenAnnouncements.add(a.id));
        this.saveSeen();
    }

    public setCurrentView(view: AnnouncementView | null): void {
        this.currentView = view;
    }

    public setInitialized(value: boolean): void {
        this.isInitialized = value;
        this.save();
    }

    public setUnreadCount(count: number): void {
        this.unreadCount = count;
    }

    public getUnseenAnnouncements(): Announcement[] {
        return this.announcements.filter((a) => !this.seenAnnouncements.has(a.id));
    }

    public getUnreadCount(): number {
        return this.unreadCount;
    }

    public setIsFreePlan(isFreePlan: boolean): void {
        this.isFreePlan = isFreePlan;
    }

    public getIsFreePlan(): boolean {
        return this.isFreePlan;
    }

    public setShowBranding(showBranding: boolean): void {
        this.showBranding = showBranding;
    }

    public getShowBranding(): boolean {
        return this.showBranding;
    }

    public setSessionToken(token: string | null): void {
        this.sessionToken = token;
        if (this.guest) {
            return;
        }
        if (token) {
            saveToStorage(this.sessionTokenKey, token);
        } else {
            removeFromStorage(this.sessionTokenKey);
        }
    }

    public getSessionToken(): string | null {
        // Guest sessions carry no session token and must never read storage.
        if (this.guest) {
            return this.sessionToken;
        }
        if (!this.sessionToken) {
            this.sessionToken = getFromStorage(this.sessionTokenKey);
        }
        return this.sessionToken;
    }

    public clear(): void {
        this.userId = null;
        this.isAnonymous = true;
        this.sessionToken = null;
        this.announcements = [];
        this.seenAnnouncements.clear();
        this.currentView = null;
        this.isInitialized = false;
        this.unreadCount = 0;

        // Guest mode wrote nothing to storage, so there is nothing to remove.
        if (this.guest) {
            return;
        }

        removeFromStorage(this.stateKey);
        removeFromStorage(this.seenKey);
        removeFromStorage(this.anonymousIdKey);
        removeFromStorage(this.sessionTokenKey);
    }

    /**
     * One-time adoption of the pre-namespacing storage keys. Older SDK builds
     * wrote fixed, un-suffixed keys; on the first run after upgrading we move the
     * device-scoped values (anonymous id, seen-set, cached state) under the new
     * namespaced keys so a returning user keeps their identity and read history.
     *
     * The session token is intentionally not migrated — init() re-issues one, and
     * a legacy token could belong to a different app sharing the origin — and the
     * legacy copies are dropped so they can't leak across apps.
     */
    private migrateLegacyKeys(): void {
        if (!this.namespaced) {
            return; // No namespace: the keys already are the legacy keys.
        }

        this.adoptLegacyKey(ANONYMOUS_ID_KEY, this.anonymousIdKey);
        this.adoptLegacyKey(SEEN_KEY, this.seenKey);
        this.adoptLegacyKey(STATE_KEY, this.stateKey);
        removeFromStorage(SESSION_TOKEN_KEY);
    }

    private adoptLegacyKey(legacyKey: string, newKey: string): void {
        if (getFromStorage(newKey) !== null) {
            return; // Already have namespaced data; leave it untouched.
        }

        const legacy = getFromStorage(legacyKey);
        if (legacy !== null) {
            saveToStorage(newKey, legacy);
            removeFromStorage(legacyKey);
        }
    }

    private load(): void {
        const state = getFromStorage(this.stateKey);
        if (state) {
            this.userId = state.userId || null;
            this.isAnonymous = state.isAnonymous !== false;
            // Don't load isInitialized - each SDK instance must call init()
            this.announcements = state.announcements || [];
        }

        const seen = getFromStorage(this.seenKey);
        if (seen && Array.isArray(seen)) {
            this.seenAnnouncements = new Set(seen);
        }

        const anonymousId = getFromStorage(this.anonymousIdKey);
        if (anonymousId) {
            this.anonymousId = anonymousId;
        }

        const sessionToken = getFromStorage(this.sessionTokenKey);
        if (typeof sessionToken === 'string') {
            this.sessionToken = sessionToken;
        }
    }

    private save(): void {
        // Guest mode keeps only in-memory runtime flags; never touch storage.
        if (this.guest) {
            return;
        }

        const state = {
            userId: this.userId,
            isAnonymous: this.isAnonymous,
            // Don't save isInitialized - it's a per-session runtime flag
            announcements: this.announcements,
        };
        saveToStorage(this.stateKey, state);
    }

    private saveSeen(): void {
        // Guest mode has no seen-set to persist.
        if (this.guest) {
            return;
        }

        // Trim to the most recently added ids (Set preserves insertion order) so
        // the persisted list can't grow without bound over a long-lived session.
        if (this.seenAnnouncements.size > MAX_SEEN) {
            const trimmed = Array.from(this.seenAnnouncements).slice(-MAX_SEEN);
            this.seenAnnouncements = new Set(trimmed);
        }

        saveToStorage(this.seenKey, Array.from(this.seenAnnouncements));
    }
}
